//** Import Modules */
import React, { useEffect, useState } from "react";
import { Grid, Layout } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";
import DashboardSidebar from "./components/DashboardSidebar";
import { Route, Routes } from "react-router";
import { useDispatch, useSelector } from "react-redux";

//** Import Components */
import SettingsPageError from "./pages/SettingsPageError";
import BusinessInfo from "./pages/BusinessInfo";
import LoadingPage from "../../common/LoadingPage";
import BusinessDescription from "./pages/BusinessDescription";
import BusinessProfile from "./pages/BusinessProfile";
import PromoteVideos from "./pages/PromoteVideos";
import BusinessQR from "./pages/BusinessQR";
import HeaderUserBar from "../../common/HeaderUserBar/HeaderUserBar";
import ManageGroups from "./pages/ManageGroups/ManageGroups";
import BusinessFollowers from "./pages/BusinessFollowers/BusinessFollowers";
import PrivateMessages from "./pages/PrivateMessages/PrivateMessages";
import ManageEvents from "./pages/ManageEvents/ManageEvents";
import ManageService from "./pages/ManageService/ManageService";
import ManageAppointments from "./pages/ManageAppointments/ManageAppointments";
import ManageProduct from "./pages/ManageRestaurants/ManageProduct";
import ManageCategories from "./pages/ManageRestaurants/ManageCategories";
import ManageAddons from "./pages/ManageRestaurants/ManageAddons";
import ManageOrders from "./pages/ManageRestaurants/ManageOrders";
import ImportProducts from "./pages/ManageRestaurants/ImportProducts";
import DeliverySettings from "./pages/ManageRestaurants/DeliverySettings";
import PickupSettings from "./pages/ManageRestaurants/PickupSettings";
import RestaurantWorkingHours from "./pages/ManageRestaurants/RestaurantWorkingHours";
import ManageAds from "./pages/ManageAds/ManageAds";
import Home from "./pages/Home";
import ManageAnalytics from "./pages/ManageAnalytics/ManageAnalytics";
import ManageReviews from "./pages/ManageReviews/ManageReviews";
import PrivateInternalRoute from "../../common/PrivateInternalRoute";
import PayoutSettings from "./pages/PayoutSettings/PayoutSettings";
import ClaimBusiness from "./pages/ClaimBusiness";

//** Import Reducers */
import { getBusiness, getBusinessList } from "../../features/reducers/business";

//** Separate antd Layout Components */
const { Header, Content, Footer } = Layout;

export default function BusinessDashboard() {
  // Setup the base url
  const baseURL = "/business";

  // Setup States
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, isMobile]);

  // Initiate action to get user's business list
  const dispatch = useDispatch();
  const isPageLoading = useSelector((state) => state.business.loadingPage);
  const businessList = useSelector((state) => state.business.businessList);
  const business = useSelector((state) => {
    return state.business.business;
  });

  useEffect(() => {
    dispatch(getBusinessList());
  }, [dispatch]);

  // Set the current business to edit
  useEffect(() => {
    const preSelectedBusiness = localStorage.getItem("editBusiness");

    if (businessList.length > 0) {
      const businessID = preSelectedBusiness
        ? preSelectedBusiness
        : businessList[0].id;

      dispatch(getBusiness(businessID));
    }
  }, [businessList, dispatch]);

  return (
    <>
      <Layout className="business-dashboard-shell" hasSider={!isMobile}>
        <DashboardSidebar
          baseURL={baseURL}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />

        <Layout
          className="dashboard-page-layout"
          style={{ marginLeft: isMobile ? 0 : collapsed ? 80 : 280 }}
        >
          <Header id="dashboard-header">
            {isMobile && (
              <button
                type="button"
                className="business-menu-toggle"
                aria-label="Open business menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="business-dashboard-navigation"
                onClick={() => setMobileMenuOpen(true)}
              >
                <FontAwesomeIcon icon={faBars} />
              </button>
            )}
            <HeaderUserBar />
          </Header>

          <Content id="dashboard-content">
            <Routes>
              {/* Home Route */}
              <Route path="/" element={<Home business={business} />} exact />

              {/* Main Business Info Forms */}
              <Route
                path="/business-information"
                element={<BusinessInfo business={business} />}
              />
              <Route
                path="/business-description"
                element={<BusinessDescription business={business} />}
              />
              <Route
                path="/profile-settings"
                element={<BusinessProfile business={business} />}
              />
              <Route
                path="/promote-videos/*"
                element={<PromoteVideos business={business} />}
              />
              <Route path="/qr" element={<BusinessQR business={business} />} />

              {/* Networking Routes */}
              <Route
                path="/groups/*"
                element={<ManageGroups business={business} />}
              />
              <Route
                path="/followers"
                element={<BusinessFollowers business={business} />}
              />
              <Route
                path="/messages/*"
                element={<PrivateMessages business={business} />}
              />
              <Route
                path="/reviews/*"
                element={<ManageReviews business={business} />}
              />

              {/* Marketing Routes */}
              <Route
                path="/events/*"
                element={
                  <PrivateInternalRoute capability="access_networking_plus">
                    <ManageEvents business={business} />
                  </PrivateInternalRoute>
                }
              />
              <Route
                path="/advertisements/*"
                element={<ManageAds business={business} />}
              />

              <Route
                path="/menu/*"
                element={<ManageProduct business={business} />}
              />

              <Route
                path="/product_categories/*"
                element={<ManageCategories business={business} />}
              />

              <Route
                path="/product_addons/*"
                element={<ManageAddons business={business} />}
              />

              <Route
                path="/orders/*"
                element={<ManageOrders business={business} />}
              />

              <Route
                path="/delivery_settings/*"
                element={<DeliverySettings business={business} />}
              />

              <Route
                path="/pickup_settings/*"
                element={<PickupSettings business={business} />}
              />

              <Route
                path="/working_hours"
                element={<RestaurantWorkingHours business={business} />}
              />

              <Route
                path="/import_products/*"
                element={<ImportProducts business={business} />}
              />

              {/* Services Routes */}
              <Route
                path="/services/*"
                element={<ManageService business={business} />}
              />

              {/* Appointments Routes */}
              <Route
                path="/appointments/*"
                element={<ManageAppointments business={business} />}
              />

              {/* Analytics Route */}
              <Route
                path="/analytics"
                element={<ManageAnalytics business={business} />}
              />

              {/* Payout Routes */}
              <Route
                path="/payout-settings"
                element={
                  <PrivateInternalRoute capability="access_networking_plus">
                    <PayoutSettings business={business} baseURL={baseURL} />
                  </PrivateInternalRoute>
                }
              />

              {/* Extra Routes */}
              <Route path="/claim-business" element={<ClaimBusiness />} />

              {/* Error Pages */}
              <Route path="*" element={<SettingsPageError />} />
            </Routes>
          </Content>
          <Footer
            style={{
              textAlign: "center",
            }}
          >
            Bizzll © 2025
          </Footer>
        </Layout>
      </Layout>

      {isPageLoading && <LoadingPage />}
    </>
  );
}
